import DATA from './data/swrHistoricalData.json';
import { SWR_FLOW_METHOD_VERSION, SWR_DEBT_METHOD_VERSION, createSWRTaxConfig, normalizeSWRTaxConfig,
  getSWRSalesPolicy, SWR_STANDARD_SALES_POLICY } from './swrTaxModel';
import { createSWRDebtConfig, normalizeSWRDebtConfig } from './swrDebtModel';

export const SWR_HISTORICAL_METHOD_VERSION = 'joint-moving-blocks-12m-annual-rebalance-v1';
export const SWR_CUSTOM_METHOD_VERSION = 'joint-blocks-12m-annual-mix-monthly-leverage-v2';
export const SWR_FUNDING_PROXY_ID = 'money-eur';
export const SWR_DEFAULT_FINANCING_SPREAD = 0.015;
export const SWR_HISTORICAL_DATA_VERSION = DATA.version;
export const SWR_HISTORICAL_DATA_HASH = DATA.manifest.contentSha256;
export const SWR_HISTORICAL_MIN_MONTHS = 60;
export const SWR_HISTORICAL_SHORT_MONTHS = 120;
export const SWR_HISTORICAL_ASSETS = DATA.assets;
export const SWR_HISTORICAL_INFLATION_REGIONS = Object.freeze({ IT: 'Italia · IPCA', EA: 'Area euro · IPCA' });
const preparedCache = new Map();
const mean = a => a.reduce((s, v) => s + v, 0) / a.length;
const nextMonth = month => {
  const [y, m] = month.split('-').map(Number);
  return `${m === 12 ? y + 1 : y}-${String(m === 12 ? 1 : m + 1).padStart(2, '0')}`;
};

export function isSWRHistoricalPortfolio(params = {}) {
  return params.historicalPortfolio?.enabled === true;
}

// Empty UI placeholders are not portfolio components; incomplete funded rows remain errors.
export function getSWRCustomDraftConfig(config) {
  return { ...config, allocations: (config.allocations || []).filter(a => a.assetId || (a.weight !== '' && a.weight !== 0)) };
}

export function createSWRHistoricalPortfolio(allocations = [], options = {}) {
  return {
    enabled: true, allocations, inflationRegion: 'IT', annualReturnHaircut: 0,
    inflationTarget: null, rebalancing: 'annual-anniversary', blockMonths: 12,
    methodVersion: SWR_CUSTOM_METHOD_VERSION, annualFinancingSpread: SWR_DEFAULT_FINANCING_SPREAD,
    datasetVersion: SWR_HISTORICAL_DATA_VERSION, datasetSha256: SWR_HISTORICAL_DATA_HASH,
    ...options,
  };
}

export function createSWRFlowPortfolio(allocations = [], options = {}) {
  return createSWRHistoricalPortfolio(allocations, { methodVersion: SWR_FLOW_METHOD_VERSION,
    rebalancing: 'sales-and-contributions-only', taxation: createSWRTaxConfig(), ...options });
}

export function createSWRDebtPortfolio(allocations = [], options = {}) {
  return createSWRFlowPortfolio(allocations, { ...options, methodVersion: SWR_DEBT_METHOD_VERSION,
    debt: createSWRDebtConfig() });
}

export function normalizeSWRHistoricalPortfolio(config, dataset = DATA) {
  if (config == null) return null;
  if (typeof config !== 'object' || Array.isArray(config) || typeof config.enabled !== 'boolean') {
    throw new Error('Opzione del portafoglio custom non valida.');
  }
  if (!config.enabled) return null;
  if (config.datasetVersion !== dataset.version || config.datasetSha256 !== dataset.manifest.contentSha256) {
    throw new Error('Il dataset storico di questo scenario non è disponibile. Conserva il risultato salvato; per ricalcolare crea una nuova versione con i dati disponibili.');
  }
  const legacy = config.methodVersion === SWR_HISTORICAL_METHOD_VERSION;
  const flow = config.methodVersion === SWR_FLOW_METHOD_VERSION;
  const debt = config.methodVersion === SWR_DEBT_METHOD_VERSION;
  const perInstrument = flow || debt;
  if ((!legacy && !perInstrument && config.methodVersion !== SWR_CUSTOM_METHOD_VERSION) || config.blockMonths !== 12
    || config.rebalancing !== (perInstrument ? 'sales-and-contributions-only' : 'annual-anniversary')) {
    throw new Error('Il metodo storico salvato non è disponibile. Nessuna sostituzione automatica delle regole.');
  }
  if (!Object.hasOwn(SWR_HISTORICAL_INFLATION_REGIONS, config.inflationRegion)) {
    throw new Error('Scegli l’inflazione IPCA Italia oppure area euro.');
  }
  if (!Number.isFinite(config.annualReturnHaircut) || config.annualReturnHaircut < 0 || config.annualReturnHaircut >= 1) {
    throw new Error('La riduzione prudenziale annua deve essere tra 0% e un valore inferiore al 100%.');
  }
  if (config.inflationTarget != null && (!Number.isFinite(config.inflationTarget) || config.inflationTarget <= -1 || config.inflationTarget > 1)) {
    throw new Error('L’inflazione di riferimento deve essere maggiore di −100% e non superiore al 100%.');
  }
  if (!Array.isArray(config.allocations) || !config.allocations.length || config.allocations.length > 22) {
    throw new Error('Aggiungi almeno una asset class; il catalogo ammette al massimo 22 componenti.');
  }
  const ids = new Set();
  let total = 0;
  const allocations = config.allocations.map(({ assetId, weight } = {}) => {
    if (!assetId) throw new Error('Scegli una asset class per ogni riga del portafoglio.');
    const asset = dataset.assets.find(a => a.id === assetId);
    if (!asset) throw new Error('Asset class non riconosciuta nel portafoglio custom.');
    if (ids.has(assetId)) throw new Error(`L’asset class ${asset.label} è presente due volte: usa una sola riga.`);
    if (asset.status !== 'available') throw new Error(`${asset.label}: serie non disponibile. ${asset.unavailableReason || ''}`);
    if (typeof weight !== 'number' || !Number.isFinite(weight)) throw new Error(`Inserisci un peso numerico per ${asset.label}; può anche essere 0%.`);
    if (weight < 0) throw new Error(`Il peso di ${asset.label} non può essere negativo.`);
    if (weight > (legacy ? 1 : 2)) throw new Error(`Riduci i pesi: il totale del portafoglio ${legacy ? 'deve essere il 100%' : 'deve essere tra 100% e 200% (leva massima 2x)'}.`);
    ids.add(assetId);
    total += weight;
    return { assetId, weight };
  }).sort((a, b) => a.assetId.localeCompare(b.assetId));
  if (legacy ? Math.abs(total - 1) > 1e-8 : total < 1 - 1e-8 || total > 2 + 1e-8) {
    throw new Error(`Le percentuali sommano ${(total * 100).toLocaleString('it-IT', { maximumFractionDigits: 6 })}%. ${legacy ? 'Questa versione richiede il 100%' : 'Il totale deve essere tra 100% e 200% (leva massima 2x)'}: non vengono normalizzate automaticamente.`);
  }
  if (flow && Math.abs(total - 1) > 1e-8) throw new Error('La fiscalità per strumento del Macroblocco A richiede il 100%, senza leva.');
  if (debt && getSWRSalesPolicy(config.taxation) !== SWR_STANDARD_SALES_POLICY) {
    throw new Error('Il metodo con debito usa l’ordine standard. La sequenza fiscale C resta disponibile senza leva nel metodo v3.');
  }
  const annualFinancingSpread = legacy || total <= 1 + 1e-8 ? 0 : config.annualFinancingSpread;
  if (!Number.isFinite(annualFinancingSpread) || annualFinancingSpread < 0 || annualFinancingSpread > 1) {
    throw new Error('Lo spread annuo del finanziamento deve essere tra 0% e 100%.');
  }
  return { ...createSWRHistoricalPortfolio(allocations), ...config, allocations, annualFinancingSpread,
    ...(perInstrument ? { taxation: normalizeSWRTaxConfig(config.taxation, allocations, debt ? 1e15 * total : 1e15) } : {}),
    ...(debt ? { debt: normalizeSWRDebtConfig(config.debt) } : {}), inflationTarget: config.inflationTarget ?? null };
}

function checkedMap(rows, label) {
  const map = new Map();
  let previous = '';
  for (const [month, logReturn] of rows) {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month) || month <= previous || !Number.isFinite(logReturn)
      || !Number.isFinite(Math.exp(logReturn)) || Math.exp(logReturn) <= 0) {
      throw new Error(`La serie ${label} contiene osservazioni non valide o non ordinate.`);
    }
    map.set(month, logReturn);
    previous = month;
  }
  return map;
}

export function longestSWRHistoricalWindow(months) {
  let best = [], current = [];
  for (const month of months) {
    if (current.length && nextMonth(current[current.length - 1]) !== month) current = [];
    current.push(month);
    // For equal lengths, retain the most recent complete segment.
    if (current.length >= best.length) best = current.slice();
  }
  return best;
}

export function applySWRPortfolioLeverage(returnRate, leverage, fundingMonthlyRate = 0) {
  if (!Number.isFinite(returnRate) || !Number.isFinite(leverage) || leverage < 1 - 1e-8 || leverage > 2 + 1e-8
    || !Number.isFinite(fundingMonthlyRate) || fundingMonthlyRate < 0) throw new Error('Ipotesi della leva non valide.');
  // Limited-equity model: residual debts and intramonth margin calls are outside SWR.
  return Math.max(-1, leverage * returnRate - (leverage - 1) * fundingMonthlyRate);
}

export function aggregateSWRHistoricalReturns(assets, weights, indices, monthlyHaircut = 1, replayMask = null, growthFactors = false, financingRates = null, annualRebalance = true) {
  const leverage = weights.reduce((s, w) => s + w, 0);
  const values = Float64Array.from(weights);
  const returns = new Float64Array(indices.length);
  for (let month = 0; month < indices.length; month++) {
    if (annualRebalance && month % 12 === 0) values.set(weights);
    let before = 0, after = 0;
    for (let a = 0; a < assets.length; a++) {
      before += values[a];
      values[a] *= growthFactors ? assets[a][indices[month]] : Math.exp(assets[a][indices[month]]);
      after += values[a];
    }
    if (!(before > 0 && after > 0 && Number.isFinite(after))) throw new Error('Il percorso storico produce un valore non valido.');
    const unlevered = after / before - 1;
    if (leverage > 1 + 1e-8 && !financingRates) throw new Error('Manca il tasso storico necessario per simulare la leva.');
    returns[month] = leverage > 1 + 1e-8
      ? (1 + applySWRPortfolioLeverage(unlevered, leverage, financingRates[indices[month]])) * (replayMask?.[month] ? 1 : monthlyHaircut) - 1
      : after / before * (replayMask?.[month] ? 1 : monthlyHaircut) - 1;
    // A common factor, taxes, withdrawals and pro-rata inflows do not change weights.
    for (let a = 0; a < assets.length; a++) values[a] /= after;
  }
  return returns;
}

function portfolioStatistics(returns) {
  const annual = [];
  for (let i = 0; i + 12 <= returns.length; i += 12) {
    let factor = 1;
    for (let m = i; m < i + 12; m++) factor *= 1 + returns[m];
    annual.push(factor - 1);
  }
  const arithmeticMean = mean(annual);
  const annualVolatility = Math.sqrt(annual.reduce((s, v) => s + (v - arithmeticMean) ** 2, 0) / (annual.length - 1));
  return { observations: annual.length, arithmeticMean, annualVolatility,
    cagr: Math.expm1(12 * mean(Array.from(returns, r => Math.log1p(r)))),
    annualReturns: annual, convention: 'complete-nonoverlapping-12m-from-common-start' };
}

function overlapWarnings(ids) {
  const selected = new Set(ids), warnings = [];
  const broad = ['eq-world', 'eq-acwi', 'eq-all-world'].filter(id => selected.has(id));
  if (broad.length > 1) warnings.push('World, ACWI e All-World condividono molte aziende: combinarli aumenta la sovrapposizione, non necessariamente la diversificazione.');
  if (broad.length && ['eq-us', 'eq-europe', 'eq-emu', 'eq-japan', 'eq-italy'].some(id => selected.has(id))) {
    warnings.push('Gli indici regionali sono già in larga parte compresi negli indici globali: la combinazione modifica il peso delle aree.');
  }
  if ((selected.has('eq-acwi') || selected.has('eq-all-world')) && selected.has('eq-emerging')) warnings.push('ACWI e All-World includono già i mercati emergenti.');
  if (selected.has('eq-europe') && (selected.has('eq-emu') || selected.has('eq-italy'))) warnings.push('Europe comprende già parte delle aziende di EMU e FTSE MIB.');
  if (selected.has('gold') && selected.has('commodities')) warnings.push('Il paniere di materie prime include anche oro.');
  if (selected.has('bond-global-aggregate') && (selected.has('bond-global-gov') || selected.has('bond-euro-gov'))) warnings.push('Global Aggregate comprende anche obbligazioni governative: alcune esposizioni possono sovrapporsi.');
  return warnings;
}

export function prepareSWRHistoricalPortfolio(input, options = {}, dataset = DATA) {
  const config = normalizeSWRHistoricalPortfolio(input, dataset);
  if (!config) return null;
  const cashProxy = Boolean(options.dynamicCashBufferEnabled && options.cashReturnMode === 'macro-proxy');
  const key = JSON.stringify([config, cashProxy]);
  if (dataset === DATA && preparedCache.has(key)) return preparedCache.get(key);
  const allocations = config.allocations.filter(a => a.weight > 0);
  const leverage = allocations.reduce((s, a) => s + a.weight, 0);
  const leveraged = leverage > 1 + 1e-8;
  const assets = allocations.map(a => dataset.assets.find(asset => asset.id === a.assetId));
  const maps = assets.map(a => checkedMap(a.monthlyLogReturns, a.label));
  const inflationMap = checkedMap(dataset.inflation[config.inflationRegion], 'IPCA');
  const cashMap = cashProxy ? checkedMap(dataset.cashDepositLogReturns, 'depositi BCE') : null;
  const fundingAsset = leveraged ? dataset.assets.find(a => a.id === SWR_FUNDING_PROXY_ID && a.status === 'available') : null;
  if (leveraged && !fundingAsset) throw new Error('La proxy monetaria necessaria per la leva non è disponibile.');
  const fundingMap = fundingAsset ? checkedMap(fundingAsset.monthlyLogReturns, 'monetario EUR per il finanziamento') : null;
  const common = [...maps[0].keys()].filter(m => maps.every(map => map.has(m)) && inflationMap.has(m) && (!cashMap || cashMap.has(m)) && (!fundingMap || fundingMap.has(m))).sort();
  const months = longestSWRHistoricalWindow(common);
  if (months.length < SWR_HISTORICAL_MIN_MONTHS) throw new Error(`Lo storico comune contiene ${months.length} mesi consecutivi. Ne servono almeno 60: cambia la composizione del portafoglio.`);
  const assetLogs = maps.map((map, i) => {
    const cost = assets[i].sourceKind === 'index-net-total-return' ? assets[i].replicationCostRate : 0;
    if (!Number.isFinite(cost) || cost < 0 || cost >= 1) throw new Error('Costo di replica dell’indice non valido.');
    return Float64Array.from(months, m => map.get(m) + Math.log1p(-cost) / 12);
  });
  const weights = allocations.map(a => a.weight);
  const indices = Int32Array.from(months, (_, i) => i);
  const financingRates = fundingMap ? Float64Array.from(months, m => Math.expm1(Math.log1p(Math.max(0, Math.expm1(12 * fundingMap.get(m)) + config.annualFinancingSpread)) / 12)) : null;
  const debt = config.methodVersion === SWR_DEBT_METHOD_VERSION;
  const flow = config.methodVersion === SWR_FLOW_METHOD_VERSION || debt;
  const statisticWeights = debt ? weights.map(w => w / leverage) : weights;
  const historicalReturns = aggregateSWRHistoricalReturns(assetLogs, statisticWeights, indices, 1, null, false, financingRates, !flow);
  const historicalStatistics = portfolioStatistics(historicalReturns);
  const monthlyHaircut = (1 - config.annualReturnHaircut) ** (1 / 12);
  const appliedStatistics = portfolioStatistics(aggregateSWRHistoricalReturns(assetLogs, statisticWeights, indices, monthlyHaircut, null, false, financingRates, !flow));
  const inflationLogs = Float64Array.from(months, m => inflationMap.get(m));
  const meanInflationLog = mean(Array.from(inflationLogs));
  const historicalInflation = Math.expm1(12 * meanInflationLog);
  const effectiveInflation = config.inflationTarget ?? historicalInflation;
  const inflationShift = Math.log1p(effectiveInflation) / 12 - meanInflationLog;
  const warnings = overlapWarnings(allocations.map(a => a.assetId));
  if (leveraged) warnings.unshift(debt
    ? 'Leva iniziale sul capitale investito; poi variabile, anche oltre 2x. Debito esplicito rimborsato con le vendite e prima degli investimenti degli apporti. Nessun nuovo credito, margine o liquidazione inframensile; il debito residuo non viene cancellato.'
    : 'La leva amplifica guadagni e perdite e paga il finanziamento. Il modello mantiene la leva mensilmente; non simula richieste di margine, debiti residui o liquidazioni inframensili.');
  if (months.length < SWR_HISTORICAL_SHORT_MONTHS) warnings.unshift(`Storico comune breve: ${months.length} mesi, meno di dieci anni. I risultati dipendono fortemente dagli episodi inclusi.`);
  if (months.length !== common.length) warnings.push('Sono presenti interruzioni nei dati: si usa il segmento comune consecutivo più lungo, senza riempire i mesi mancanti.');
  const crises = [{ id: '2008', label: 'Crisi 2008', start: '2008-01', end: '2009-12' },
    { id: '2020', label: 'Pandemia 2020', start: '2020-01', end: '2020-12' },
    { id: '2022', label: 'Inflazione e ribassi 2022', start: '2022-01', end: '2022-12' }]
    .map(c => ({ ...c, included: months[0] <= c.start && months[months.length - 1] >= c.end }));
  const summary = {
    methodVersion: config.methodVersion, datasetVersion: config.datasetVersion, datasetSha256: config.datasetSha256,
    ...(flow ? { taxation: config.taxation, statisticsConvention: debt ? 'unlevered-gross-assets-buy-and-hold-descriptive-only' : 'buy-and-hold-no-cashflows-descriptive-only' } : {}),
    ...(debt ? { debt: config.debt } : {}),
    periodStart: months[0], periodEnd: months[months.length - 1], observations: months.length,
    inflationRegion: config.inflationRegion, historicalInflation, effectiveInflation,
    historicalStatistics, appliedStatistics, annualReturnHaircut: config.annualReturnHaircut,
    rebalancing: config.rebalancing, blockMonths: config.blockMonths, cashProxyIncluded: cashProxy,
    leverage: leveraged ? leverage : 1, annualFinancingSpread: config.annualFinancingSpread,
    financing: fundingAsset ? { proxyId: fundingAsset.id, label: fundingAsset.label, isin: fundingAsset.isin,
      source: fundingAsset.source, convention: 'annualized-xeon-nav-plus-spread-floor-zero-effective-monthly-v1',
      meanAnnualBaseRate: mean(months.map(m => Math.expm1(12 * fundingMap.get(m)))),
      meanAnnualFundingRate: mean(Array.from(financingRates, r => Math.expm1(12 * Math.log1p(r)))),
      leverageMaintenance: debt ? 'variable-no-redraw' : 'monthly', limitedEquity: !debt } : null,
    assets: allocations.map((a, i) => ({ ...a, label: assets[i].label, isin: assets[i].isin,
      benchmark: assets[i].benchmark, hedged: assets[i].hedged, navCurrency: assets[i].navCurrency,
      sourceKind: assets[i].sourceKind, source: assets[i].source, fundCostsIncluded: assets[i].fundCostsIncluded,
      replicationCostRate: assets[i].replicationCostRate, notes: assets[i].notes || null })),
    warnings, crises,
    limitations: flow ? 'Fiscalità per strumento in regime amministrato italiano, profili congelati come ipotesi futura. Ribilanciamento soltanto attraverso vendite necessarie e apporti; quantità equivalenti frazionarie, senza commissioni di negoziazione. Il bootstrap non certifica il rischio futuro.'
      : 'Portafoglio modello con fiscalità aggregata; compravendite interne di ribilanciamento senza imposte o commissioni. Il bootstrap non genera crisi mai osservate e non certifica il rischio futuro.',
  };
  const prepared = { config, months, assetLogs, assetGrowthFactors: assetLogs.map(a => Float64Array.from(a, Math.exp)), weights, inflationLogs, inflationShift, monthlyHaircut,
    cashLogs: cashMap ? Float64Array.from(months, m => cashMap.get(m)) : null, financingRates, summary };
  if (dataset === DATA) {
    if (preparedCache.size >= 32) preparedCache.delete(preparedCache.keys().next().value);
    preparedCache.set(key, prepared);
  }
  return prepared;
}

export function sampleSWRHistoricalIndices(observations, totalMonths, random, blockMonths = 12) {
  if (!Number.isInteger(observations) || !Number.isInteger(totalMonths) || totalMonths < 0
    || !Number.isInteger(blockMonths) || blockMonths < 1 || observations < blockMonths || typeof random !== 'function') {
    throw new Error('Parametri del ricampionamento storico non validi.');
  }
  const indices = new Int32Array(totalMonths);
  for (let pos = 0; pos < totalMonths; pos += blockMonths) {
    const draw = random();
    if (!Number.isFinite(draw) || draw < 0 || draw >= 1) throw new Error('Estrazione casuale storica non valida.');
    const start = Math.floor(draw * (observations - blockMonths + 1));
    for (let i = 0; i < Math.min(blockMonths, totalMonths - pos); i++) indices[pos + i] = start + i;
  }
  return indices;
}

export function generateSWRHistoricalPath(prepared, params, random, options = {}) {
  const preStartMonths = (params.planningMode === 'future' || params.futurePlanningEnabled ? params.yearsUntilStart || 0 : 0) * 12;
  const simulationMonths = params.yearsToSimulate * 12;
  const indices = sampleSWRHistoricalIndices(prepared.months.length, preStartMonths + simulationMonths, random, options.blockMonths ?? 12);
  const replayMask = new Uint8Array(simulationMonths);
  if (options.replay) {
    const start = prepared.months.indexOf(options.replay.start);
    const end = prepared.months.indexOf(options.replay.end);
    if (start < 0 || end < start) throw new Error('La finestra di replay non è interamente nello storico comune.');
    for (let m = 0; m < Math.min(simulationMonths, end - start + 1); m++) {
      indices[preStartMonths + m] = start + m;
      replayMask[m] = 1;
    }
  }
  const inflationRates = new Float64Array(indices.length), grossCashRates = new Float64Array(indices.length);
  for (let m = 0; m < indices.length; m++) {
    const replay = m >= preStartMonths && replayMask[m - preStartMonths];
    inflationRates[m] = Math.expm1(prepared.inflationLogs[indices[m]] + (replay ? 0 : prepared.inflationShift));
    if (prepared.cashLogs) grossCashRates[m] = Math.expm1(prepared.cashLogs[indices[m]]);
  }
  return {
    monthlyReturns: aggregateSWRHistoricalReturns(prepared.assetGrowthFactors,
      prepared.config.methodVersion === SWR_DEBT_METHOD_VERSION ? prepared.weights.map(w => w / prepared.summary.leverage) : prepared.weights,
      indices.subarray(preStartMonths), prepared.monthlyHaircut, replayMask, true, prepared.financingRates,
      ![SWR_FLOW_METHOD_VERSION, SWR_DEBT_METHOD_VERSION].includes(prepared.config.methodVersion)),
    ...([SWR_FLOW_METHOD_VERSION, SWR_DEBT_METHOD_VERSION].includes(prepared.config.methodVersion) ? { componentPath: {
      indices: indices.subarray(preStartMonths), growthFactors: prepared.assetGrowthFactors,
      monthlyHaircut: prepared.monthlyHaircut, replayMask,
      ...(prepared.config.methodVersion === SWR_DEBT_METHOD_VERSION ? { financingMonthlyRates: Float64Array.from(indices.subarray(preStartMonths), i => prepared.financingRates?.[i] ?? 0) } : {}),
    } } : {}),
    ...(prepared.financingRates ? { financingMonthlyRates: Float64Array.from(indices.subarray(preStartMonths), i => prepared.financingRates[i]) } : {}),
    inflationRates, grossCashRates, preStartMonths,
    ...(options.includeIndices ? { historicalIndices: indices } : {}),
  };
}

export function getSWRHistoricalReplayDefinitions(prepared) {
  return [
    { id: 'historical-joint-2008', label: 'Crisi storica 2008–2009', start: '2008-01', end: '2009-12' },
    { id: 'historical-joint-2020-2022', label: 'Sequenza storica 2020–2022', start: '2020-01', end: '2022-12' },
  ].filter(r => prepared.months[0] <= r.start && prepared.months[prepared.months.length - 1] >= r.end);
}
